import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Injector,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  booleanAttribute,
  inject,
  input,
  output,
  computed,
  signal,
  viewChild,
  ElementRef,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import clsx from 'clsx';
import {
  WithDefaultValidatorProps,
  composeValidatorFn,
  hasChangedAnyDefaultValidatorProps,
} from '../utils/defaultValidator';
import { UiFormReactiveConfigService } from '../form-reactive-config.service';

let INPUT_UID = 0;

const DRAG_THRESHOLD_PX = 8;
const TAP_MAX_DURATION_MS = 250;
const CLICK_SUPPRESSION_MS = 100;

export type InputSwitchLabelSize = 'xs' | 'sm' | 'md';

export type InputSwitchValueType = boolean | undefined | null;

@Component({
  selector: 'pxw-input-switch',
  templateUrl: './input-switch.component.html',
  styleUrl: './input-switch.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: InputSwitchComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: InputSwitchComponent,
    },
  ],
  imports: [NgClass],
})
export class InputSwitchComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator, WithDefaultValidatorProps
{
  @Input({ required: true }) name: string;
  @Input() label: string;
  @Input() labelSize: InputSwitchLabelSize = 'sm';
  @Input() helper: string;
  @Input() legend: string;
  @Input() messages: { [key: string]: string } = {};
  @Input() displayErrors = false;

  @Input({ transform: booleanAttribute }) required?: boolean;
  @Input({ transform: booleanAttribute }) requiredTrue?: boolean;
  readonly forceControlled = input(false);
  readonly ariaLabel = input<string>();

  readonly toggleRequested = output<boolean>();

  private injector = inject(Injector);

  protected value: InputSwitchValueType;
  protected inputId: string;
  protected isDisabled: boolean;

  protected readonly knobElement = viewChild<ElementRef<HTMLInputElement>>('input');
  protected readonly dragOffset = signal<number | null>(null);
  protected readonly isInteracted = signal(false);

  private dragPointerId: number | null = null;
  private dragStartX = 0;
  private dragTravel = 0;
  private dragMaxOffset = 0;
  private dragStartTime = 0;
  private suppressClickUntil = 0;

  protected readonly knobTransform = computed(() => {
    const offset = this.dragOffset();

    if (offset === null) {
      return null;
    }
    const base = this.value === true ? this.dragTravel : 0;
    const position = Math.min(this.dragTravel, Math.max(0, base + offset));

    return `translateX(${position}px)`;
  });

  private onChange: (value: InputSwitchValueType) => void;
  private onTouched: () => void;
  private onValidatorChange: () => void;
  private validatorFn: ValidatorFn | null;
  private formReactiveConfigService = inject(UiFormReactiveConfigService);

  @HostBinding('class') get classNames() {
    return clsx('ui-input', `ui-input-switch`, {
      'ui-input--with-helper': this.helper,
      'ui-input--with-errors': !this.isValid,
    });
  }

  get formControl() {
    const ngControl = this.injector.get(NgControl, null);

    if (ngControl) {
      return ngControl.control as FormControl;
    }
    return null;
  }

  get isRequired() {
    return (
      this.formControl?.hasValidator(Validators.required) ||
      this.required === true ||
      this.formControl?.hasValidator(Validators.requiredTrue) ||
      this.requiredTrue === true
    );
  }

  get isChecked() {
    return this.value === true ? true : null;
  }

  get isValid() {
    return this.formControl?.valid;
  }

  get isTouched() {
    return this.formControl?.touched;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get errorEntries(): [string, any][] {
    return Object.entries(this.formControl?.errors || {});
  }

  get shouldDisplayErrors() {
    return (this.displayErrors || this.isTouched) && !this.isValid && !this.isDisabled;
  }

  ngOnInit(): void {
    this.inputId = `input-switch-${++INPUT_UID}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (hasChangedAnyDefaultValidatorProps(changes)) {
      this.validatorFn = composeValidatorFn(this);

      if (this.formControl) {
        this.formControl.updateValueAndValidity();
      }
    }
  }

  writeValue(value: InputSwitchValueType) {
    this.value = value || null;
  }
  registerOnChange(onChange: (value: InputSwitchValueType) => void) {
    this.onChange = onChange;
  }
  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validate(control: AbstractControl): ValidationErrors | null {
    return this.validatorFn?.(control) || null;
  }
  registerOnValidatorChange(onValidatorChange: () => void): void {
    this.onValidatorChange = onValidatorChange;
  }

  onToggle($event: Event) {
    if (this.isDisabled) {
      return;
    }
    this.isInteracted.set(true);
    if ($event.timeStamp < this.suppressClickUntil) {
      this.suppressClickUntil = 0;
      $event.preventDefault();
      return;
    }
    this.applyToggle(this.value ? false : true, $event);
  }

  onDragStart($event: PointerEvent) {
    const isSecondaryPointer = $event.button !== 0 || $event.isPrimary === false;

    if (this.isDisabled || this.dragPointerId !== null || isSecondaryPointer) {
      return;
    }
    const track = $event.currentTarget as HTMLElement;

    track.setPointerCapture?.($event.pointerId);

    this.isInteracted.set(true);

    this.suppressClickUntil = 0;
    this.dragPointerId = $event.pointerId;
    this.dragStartX = $event.clientX;
    this.dragStartTime = $event.timeStamp;
    this.dragTravel = track.clientWidth - (this.knobElement()?.nativeElement.offsetWidth ?? 0);
    this.dragMaxOffset = 0;
    this.dragOffset.set(0);
  }

  onDragMove($event: PointerEvent) {
    if (this.dragPointerId !== $event.pointerId || this.isDisabled) {
      return;
    }
    const offset = $event.clientX - this.dragStartX;

    this.dragMaxOffset = Math.max(this.dragMaxOffset, Math.abs(offset));
    this.dragOffset.set(offset);
  }

  onDragEnd($event: PointerEvent) {
    if (this.dragPointerId !== $event.pointerId) {
      return;
    }
    const offset = this.dragOffset() ?? 0;
    const travelled = this.dragMaxOffset;
    const elapsed = $event.timeStamp - this.dragStartTime;

    this.releaseDrag($event);

    if (this.isDisabled) {
      return;
    }
    const base = this.value === true ? this.dragTravel : 0;
    const position = Math.min(this.dragTravel, Math.max(0, base + offset));
    const nextValue = position > this.dragTravel / 2;
    const crossed = nextValue !== (this.value === true);
    const deliberateDrag = travelled >= DRAG_THRESHOLD_PX && elapsed >= TAP_MAX_DURATION_MS;

    if (!crossed && !deliberateDrag) {
      return;
    }
    this.suppressClickUntil = $event.timeStamp + CLICK_SUPPRESSION_MS;

    if (!crossed) {
      return;
    }
    this.applyToggle(nextValue, $event);
  }

  onDragCancel($event: PointerEvent) {
    if (this.dragPointerId !== $event.pointerId) {
      return;
    }
    this.releaseDrag($event);
  }

  private releaseDrag($event: PointerEvent) {
    const track = $event.currentTarget as HTMLElement;
    const pointerId = this.dragPointerId;

    this.dragPointerId = null;
    this.dragOffset.set(null);

    if (pointerId !== null && track?.hasPointerCapture?.(pointerId)) {
      track.releasePointerCapture(pointerId);
    }
  }

  private applyToggle(nextValue: boolean, $event: Event) {
    if (this.forceControlled()) {
      $event.preventDefault();
      this.onTouched?.();
      this.toggleRequested.emit(nextValue);
      return;
    }
    this.writeValue(nextValue);
    this.onChange(nextValue);
    this.onValidatorChange();
    this.onTouched();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected getErrorMessage([key, data]: [string, any]) {
    return this.messages?.[key] || this.formReactiveConfigService.getErrorMessage(key, data);
  }
}
